import {
  Alert,
  AppBar,
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Toolbar,
  Typography,
  Grid
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Input, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CloseIcon from "@mui/icons-material/Close";
import HelpIcon from "@mui/icons-material/Help";
import HistoryIcon from "@mui/icons-material/History";
import {
  DataGridPremium,
  GridCsvExportMenuItem,
  GridExcelExportMenuItem,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import _debounce from "lodash/debounce";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import FieldNames from "../../../../defaults/FieldNames.json";
import GridDefinitions from "../../../../defaults/GridDefinitions.json";
import AuditLogService from "../../../../services/AuditLogService";
import BudgetService from "../../../../services/BudgetService";
import ErrorService from "../../../../services/ErrorService";
import IDService from "../../../../services/IDService";
import {
  calculateFiscalYearQuarter, formatDateForDisplay,
  getSaveableDate
} from "../../../../utils/DateHelper";
import { currencyFormat, currencyFormatter, removeDecimalWithoutDefault, removeLettersWithoutDefault, removeNegativeWithoutDefault } from "../../../../utils/FormatHelper";
import BasicTextField from "../../../details/customPanels/BasicTextField";
import InFormGenericModal from "../../../InFormGenericModal";
import { MuiMenuDropdown } from "../../../MuiMenuDropdown";
import WorkflowFiscalYearFilter from "./WorkflowFiscalYearFilter";
import WorkflowGridFilters from "./WorkflowGridFilters";

const BlueAlien = "#1976d2";
const WhiteAlien = "#ffffff";

export default function BudgetManagementDialog({
  currentUserInfo,
  userRoleInfo,
  setShowMap,
  headerheight
}) {
  const navigate = useNavigate();
  const config = useSelector((state) => state.configuration.config);
  const apiRef = useGridApiRef();
  const gridActionMenuRef = useRef();
  const dispatch = useDispatch();
  const rootServiceUrl = useSelector(
    (state) => state.configuration.rootServiceUrl
  );
  const useStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
    condensedFormControl: {
      margin: "5px 15px 5px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControl: {
      margin: "10px 15px 10px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControlHeader: {
      margin: "0px 15px 0px 15px",
      minWidth: 120,
      width: "90%",
    },
    snackBarStyle: {
      top: "120px !important",
    },
  });
  const classes = useStyles();
  const nifcToken = useSelector((state) => state.configuration.nifcToken);

  const WhiteAlien = "#ffffff";
  const BlueAlien = "#1976d2";
  const LightGrayAlien = "#f8f8f8";

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isFailure, setIsFailure] = useState(false);
  const userApproverRole = userRoleInfo?.[0]?.[FieldNames.userRoles.appRole];
  const canEditDepartmentLevel = userApproverRole === FieldNames.roles.department;
  const validAgencyRoles = [FieldNames.roles.department, FieldNames.roles.agency];
  const canEditAgencyLevel = validAgencyRoles.some(arrVal => arrVal === userApproverRole);
  const validRegionRoles = [...validAgencyRoles, FieldNames.roles.region]
  const canEditRegionLevel = validRegionRoles.some(arrVal => arrVal === userApproverRole);
  const validUnitRoles = [...validRegionRoles, FieldNames.roles.local]
  const canEditUnitLevel = validUnitRoles.some(arrVal => arrVal === userApproverRole);

  const [gridColumns, setGridColumns] = useState([]);
  const [gridRows, setGridRows] = useState([]);
  const [isGridLoading, setIsGridLoading] = useState(false);
  const [allSelectedItems, setAllSelectedItems] = useState([]);
  const gridSelectionLength =
    allSelectedItems && allSelectedItems.length > 0
      ? allSelectedItems.length
      : 0;
  const [selectedBudgetTotal, setSelectedBudgetTotal] = useState(0);
  const budgetManagementGridColDefinitions = [
    GridDefinitions.BudgetManagementGrid.name,
    GridDefinitions.BudgetManagementGrid.subunit,
    GridDefinitions.BudgetManagementGrid.tribe,
    GridDefinitions.BudgetManagementGrid.regBudget,
    GridDefinitions.BudgetManagementGrid.bilBudget,
    GridDefinitions.BudgetManagementGrid.disasterReliefBudget,
    GridDefinitions.BudgetManagementGrid.totalBudget,
  ];
  const budgetAuditLogGridColDefinitions = [
    GridDefinitions.UserAuditLog.modifiedBy,
    GridDefinitions.UserAuditLog.modifiedOn,
    GridDefinitions.UserAuditLog.modification,
  ];

  const theme = createTheme({
    overrides: {
      palette: {
        red: {
          main: "#FF0000",
          light: "#FF0000",
          dark: "#FF0000",
          contrastText: "#242105",
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: "green",
        },
      },
      MuiOutlinedInput: {
        root: {
          "& $notchedOutline": {
            //initial border
            borderColor: BlueAlien,
          },
          "&:hover $notchedOutline": {
            //border on hover
            borderColor: BlueAlien,
            border: "2px solid",
          },
          "&$focused $notchedOutline": {
            //border on focus
            borderColor: BlueAlien,
            border: "2px solid",
          },
        },
      },
    },
  });

  const [fundingDepartmentSelection, setFundingDepartmentSelection] =
    useState(null);
  const [fundingAgencySelection, setFundingAgencySelection] = useState(null);
  const [fundingRegionSelection, setFundingRegionSelection] = useState(null);
  const [fundingUnitSelection, setFundingUnitSelection] = useState(null);
  const [fundingSubUnitSelection, setFundingSubUnitSelection] = useState(null);
  const [fundingTribeSelection, setFundingTribeSelection] = useState(null);
  const [entityTypeSelection, setEntityTypeSelection] = useState(null);
  const currLevelLabel =
    fundingTribeSelection &&
    fundingTribeSelection[FieldNames.budgetFields.tribe]
      ? "Tribe"
      : fundingSubUnitSelection &&
        fundingSubUnitSelection[FieldNames.budgetFields.subUnit]
      ? "SubUnit"
      : fundingUnitSelection &&
        fundingUnitSelection[FieldNames.budgetFields.unit]
      ? "Unit"
      : fundingRegionSelection &&
        fundingRegionSelection[FieldNames.budgetFields.region]
      ? "Region"
      : fundingAgencySelection &&
        fundingAgencySelection[FieldNames.budgetFields.agency]
      ? "Agency"
      : fundingDepartmentSelection &&
        fundingDepartmentSelection[FieldNames.budgetFields.department]
      ? "Department"
      : "";
  const currentSelectedLevel =
    currLevelLabel === "Department"
      ? fundingDepartmentSelection[FieldNames.budgetFields.department]
      : currLevelLabel === "Agency"
      ? fundingAgencySelection[FieldNames.budgetFields.agency]
      : currLevelLabel === "Region"
      ? fundingRegionSelection[FieldNames.budgetFields.region]
      : currLevelLabel === "Unit"
      ? fundingUnitSelection[FieldNames.budgetFields.unit]
      : currLevelLabel === "SubUnit"
      ? fundingSubUnitSelection[FieldNames.budgetFields.subUnit]
      : currLevelLabel === "Tribe"
      ? fundingTribeSelection[FieldNames.budgetFields.tribe]
      : "Master Level";

  const canUserEditRows = (currLevelLabel === "Department" && canEditDepartmentLevel) ||
    (currLevelLabel === "Agency" && canEditAgencyLevel) ||
    (currLevelLabel === "Region" && canEditRegionLevel) ||
    ((currLevelLabel === "Unit" || currLevelLabel === "SubUnit" || currLevelLabel === "Tribe") && canEditUnitLevel);

  const [fundingUnitTypeSelection, setFundingUnitTypeSelection] =
    useState(null);
  const [fiscalYearSelection, setFiscalYearSelection] = useState(null);
  const [fiscalYearOptions, setFiscalYearOptions] = useState([]);

  // Calculated Budget Values
  const [editedBudgetValue, setEditedBudgetValue] = useState(0);
  const [editedRegularBudgetValue, setEditedRegularBudgetValue] = useState(0);
  const [editedBILBudgetValue, setEditedBILBudgetValue] = useState(0);
  const [editedDisasterReliefBudgetValue, setEditedDisasterReliefBudgetValue] =
    useState(0);
  const gridRowsBudgetRegular = gridRows
    .map((item) => item[FieldNames.budgetFields.regularBudget])
    .reduce((a, b) => a + b, 0);
  const gridRowsBudgetBil = gridRows
    .map((item) => item[FieldNames.budgetFields.bilBudget])
    .reduce((a, b) => a + b, 0);
  const gridRowsBudgetDisasterRelief = gridRows
    .map((item) => item[FieldNames.budgetFields.disasterReliefBudget])
    .reduce((a, b) => a + b, 0);
  const gridRowsBudgetTotal = gridRows
    .map((item) => item[FieldNames.budgetFields.constructedTotalBudget])
    .reduce((a, b) => a + b, 0);
  const [totalBudgetValue, setTotalBudgetValue] = useState(0);
  const [regularBudgetValue, setRegularBudgetValue] = useState(0);
  const [bilBudgetValue, setBilBudgetValue] = useState(0);
  const [disasterReliefBudget, setDisasterReliefBudget] = useState(0);
  const remainingRegularBudgetValue =
    regularBudgetValue - gridRowsBudgetRegular;
  const remainingBILBudgetValue = bilBudgetValue - gridRowsBudgetBil;
  const remainingDisasterReliefBudgetValue =
    disasterReliefBudget - gridRowsBudgetDisasterRelief;
  const remainingTotalBudgetValue = totalBudgetValue - gridRowsBudgetTotal;

  // Formatted budget values
  const formattedRegularBudgetAllocation = currencyFormat(removeLettersWithoutDefault(removeDecimalWithoutDefault(regularBudgetValue)));
  const formattedBilBudgetAllocation = currencyFormat(removeLettersWithoutDefault(removeDecimalWithoutDefault(bilBudgetValue)));
  const formattedDisasterReliefBudgetAllocation = currencyFormat(removeLettersWithoutDefault(removeDecimalWithoutDefault(disasterReliefBudget)));
  const formattedTotalBudgetAllocation = currencyFormat(removeLettersWithoutDefault(removeDecimalWithoutDefault(totalBudgetValue)));
  const formattedRemainingRegularBudgetValue = currencyFormat(removeLettersWithoutDefault(removeDecimalWithoutDefault(remainingRegularBudgetValue)));
  const formattedRemainingBilBudgetValue = currencyFormat(removeLettersWithoutDefault(removeDecimalWithoutDefault(remainingBILBudgetValue)));
  const formattedRemainingDisasterReliefBudgetValue = currencyFormat(removeLettersWithoutDefault(removeDecimalWithoutDefault(remainingDisasterReliefBudgetValue)));
  const formattedRemainingTotalBudgetValue = currencyFormat(removeLettersWithoutDefault(removeDecimalWithoutDefault(remainingTotalBudgetValue)));

  const [currentRow, setCurrentRow] = useState(null);
  const [budgetBucketOpen, setBudgetBucketOpen] = useState(false);
  const [budgetBucketOptions, setBudgetBucketOptions] = useState([
    FieldNames.budgetBuckets.regular,
    FieldNames.budgetBuckets.bil,
    FieldNames.budgetBuckets.disasterRelief,
  ]);
  const budgetBucketLoading =
    budgetBucketOpen && budgetBucketOptions.length === 0;
  const [budgetBucketSelection, setBudgetBucketSelection] = useState(null);
  const [budgetEditorOpen, setBudgetEditorOpen] = useState(false);
  const [budgetReasoning, setBudgetReasoning] = useState("");

  const [editedBudgetBulkValue, setEditedBudgetBulkValue] = useState(0);
  const [budgetBucketBulkOpen, setBudgetBucketBulkOpen] = useState(false);
  const budgetBucketBulkLoading =
    budgetBucketBulkOpen && budgetBucketOptions.length === 0;
  const [budgetBucketBulkSelection, setBudgetBucketBulkSelection] =
    useState(null);
  const [budgetBulkReasoning, setBudgetBulkReasoning] = useState("");

  const [budgetHistoryOpen, setBudgetHistoryOpen] = useState(false);
  const [budgetHistoryRows, setBudgetHistoryRows] = useState([]);
  const [budgetHistoryCols, setBudgetHistoryCols] = useState([]);

  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    SubUnit: false,
    Tribe: false,
  });
  const [selectionModel, setSelectionModel] = useState({ids: new Set(), type: 'include'});
  const [aggregationModel, setAggregationModel] = useState({
    TotalBudget: "sum",
    RegularBudget: "sum",
    BILBudget: "sum",
    DisasterReliefBudget: "sum",
  });

  useEffect(() => {
    setShowMap(false);
  }, [setShowMap]);

  useEffect(() => {
    let fyList = [];
    let pastFyList = [];
    let futureFyList = [];
    let currFY = calculateFiscalYearQuarter(new Date(), true);
    if (currFY) {
      if (currFY < 2024) {
        currFY = 2024;
      }

      for (let i = 1; i <= 5; i++) {
        if (currFY - i >= 2024) {
          pastFyList.unshift(currFY - i);
        }
        futureFyList.push(currFY + i);
      }
    }

    fyList = [...pastFyList, currFY, ...futureFyList];
    fyList = fyList.map((year) => {
      return { FiscalYear: `${year}` };
    });
    setFiscalYearOptions(fyList);
    setFiscalYearSelection({ FiscalYear: `${currFY}` });
  }, [config, rootServiceUrl]);

  //
  // Manage Grid Rows
  //

  useEffect(() => {
    if (
      !entityTypeSelection ||
      !fiscalYearSelection ||
      (!fundingDepartmentSelection &&
        !fundingAgencySelection &&
        !fundingRegionSelection &&
        !fundingUnitSelection &&
        !entityTypeSelection)
    ) {
      return;
    }

    establishGridColumns();
    getGridBudgetRows();
    BudgetService.getSelectedBudget(
      fiscalYearSelection,
      fundingDepartmentSelection,
      fundingAgencySelection,
      fundingRegionSelection,
      fundingUnitSelection,
      fundingSubUnitSelection,
      fundingTribeSelection,
      entityTypeSelection
    ).then((resp) => {
      if (resp && resp.length > 0) {
        let budgetValues = resp[0];
        let regBudget = budgetValues[FieldNames.budgetFields.regularBudget];
        let bilBudget = budgetValues[FieldNames.budgetFields.bilBudget];
        let disBudget =
          budgetValues[FieldNames.budgetFields.disasterReliefBudget];
        let totalBudget = regBudget + bilBudget + disBudget;
        setRegularBudgetValue(regBudget);
        setBilBudgetValue(bilBudget);
        setDisasterReliefBudget(disBudget);
        setTotalBudgetValue(totalBudget);
      } else {
        setRegularBudgetValue(0);
        setBilBudgetValue(0);
        setDisasterReliefBudget(0);
        setTotalBudgetValue(0);
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
    });
  }, [
    config,
    rootServiceUrl,
    fiscalYearSelection,
    fundingDepartmentSelection,
    fundingAgencySelection,
    fundingRegionSelection,
    fundingUnitSelection,
    fundingSubUnitSelection,
    fundingTribeSelection,
    fundingUnitTypeSelection,
    entityTypeSelection
  ]);

  const getGridBudgetRows = () => {
    setIsGridLoading(true);
    let tempBudgetRows = [];
    // Query rows for grid
    if (
      !fundingDepartmentSelection ||
      !fundingDepartmentSelection[FieldNames.budgetFields.department]
    ) {
      BudgetService.getBudgetDepartments().then(
        (resp) => {
          if (resp && resp.length > 0) {
            tempBudgetRows = resp;
          } else {
            tempBudgetRows = [];
          }
          getExistingBudgetValues(tempBudgetRows);
        }
      ).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else if (
      !fundingAgencySelection ||
      !fundingAgencySelection[FieldNames.budgetFields.agency]
    ) {
      BudgetService.getBudgetAgencyByDepartment(
        fundingDepartmentSelection,
      ).then((resp) => {
        if (resp && resp.length > 0) {
          tempBudgetRows = resp;
        } else {
          tempBudgetRows = [];
        }
        getExistingBudgetValues(tempBudgetRows);
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else if (
      !fundingRegionSelection ||
      !fundingRegionSelection[FieldNames.budgetFields.region]
    ) {
      BudgetService.getBudgetRegionsByAgency(
        fundingDepartmentSelection,
        fundingAgencySelection
      ).then((resp) => {
        if (resp && resp.length > 0) {
          tempBudgetRows = resp;
        } else {
          tempBudgetRows = [];
        }
        getExistingBudgetValues(tempBudgetRows);
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else {
      BudgetService.getBudgetUnits(
        fundingDepartmentSelection,
        fundingAgencySelection,
        fundingRegionSelection,
        fundingUnitSelection,
        fundingSubUnitSelection,
        fundingTribeSelection
      ).then((resp) => {
        if (resp && resp.length > 0) {
          tempBudgetRows = resp;
        } else {
          tempBudgetRows = [];
        }
        getExistingBudgetValues(tempBudgetRows);
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
    setSelectionModel({ids: new Set(), type: 'include'});
  };

  const getExistingBudgetValues = (tempGridRows) => {
    BudgetService.getAllBudgetEntries(
      fiscalYearSelection,
      fundingDepartmentSelection,
      fundingAgencySelection,
      fundingRegionSelection,
      fundingUnitSelection,
      fundingSubUnitSelection,
      fundingTribeSelection,
      entityTypeSelection
    ).then((resp) => {
      if (resp && resp.length > 0) {
        let completedRows = tempGridRows.map(
          (obj) =>
            resp.find(
              (o) =>
                (obj[FieldNames.budgetFields.department] ===
                  o[FieldNames.budgetFields.department] ||
                  (obj[FieldNames.budgetFields.department] &&
                    obj[FieldNames.budgetFields.department][
                      FieldNames.budgetFields.department
                    ] === o[FieldNames.budgetFields.department])) &&
                (obj[FieldNames.budgetFields.agency] ===
                  o[FieldNames.budgetFields.agency] ||
                  (obj[FieldNames.budgetFields.agency] &&
                    obj[FieldNames.budgetFields.agency][
                      FieldNames.budgetFields.agency
                    ] === o[FieldNames.budgetFields.agency])) &&
                (obj[FieldNames.budgetFields.region] ===
                  o[FieldNames.budgetFields.region] ||
                  (obj[FieldNames.budgetFields.region] &&
                    obj[FieldNames.budgetFields.region][
                      FieldNames.budgetFields.region
                    ] === o[FieldNames.budgetFields.region])) &&
                (obj[FieldNames.budgetFields.unit] ===
                  o[FieldNames.budgetFields.unit] ||
                  (obj[FieldNames.budgetFields.unit] &&
                    obj[FieldNames.budgetFields.unit][
                      FieldNames.budgetFields.unit
                    ] === o[FieldNames.budgetFields.unit])) &&
                (obj[FieldNames.budgetFields.subUnit] ===
                  o[FieldNames.budgetFields.subUnit] ||
                  (obj[FieldNames.budgetFields.subUnit] &&
                    obj[FieldNames.budgetFields.subUnit][
                      FieldNames.budgetFields.subUnit
                    ] === o[FieldNames.budgetFields.subUnit])) &&
                (obj[FieldNames.budgetFields.tribe] ===
                  o[FieldNames.budgetFields.tribe] ||
                  (obj[FieldNames.budgetFields.tribe] &&
                    obj[FieldNames.budgetFields.tribe][
                      FieldNames.budgetFields.tribe
                    ] === o[FieldNames.budgetFields.tribe]))
            ) || obj
        );

        let finalRows = completedRows.map((row, index) => {
          row.index = index;
          return row;
        });

        setGridRows(finalRows);
      } else {
        let finalRows = tempGridRows.map((row, index) => {
          row.index = index;
          return row;
        });
        setGridRows(finalRows);
      }
      setIsGridLoading(false);
    }).catch(error => {
      ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
    });
  };

  const establishGridColumns = () => {
    let columns = [];
    budgetManagementGridColDefinitions.forEach((colDef) => {
      columns.push({
        field: colDef.value,
        headerName: colDef.header,
        type: colDef.type,
        hideable: colDef.hideable,
        sortable: colDef.sortable,
        editable: colDef.editable,
        flex: colDef.flex,
        align: colDef.align,
        disableExport: colDef.disableExport,
        aggregable:
          colDef.value === "TotalBudget" ||
          colDef.value === "RegularBudget" ||
          colDef.value === "BILBudget" ||
          colDef.value === "DisasterReliefBudget",
        valueFormatter: (value) => {
          if (
            !value ||
            (colDef.value !== "TotalBudget" &&
              colDef.value !== "RegularBudget" &&
              colDef.value !== "BILBudget" &&
              colDef.value !== "DisasterReliefBudget")
          ) {
            return value;
          }
          return currencyFormatter.format(value);
        },
      });
    });

    columns.push({
      field: "actions",
      headerName: "Actions",
      hideable: true,
      sortable: false,
      editable: false,
      flex: 0.75,
      align: "center",
      disableExport: true,
      // Render Cell allows you to set entire components within a cell for custom buttons/functionality
      renderCell: (params) => {
        return params.rowNode.type === 'group' || params.rowNode.type === 'pinnedRow' ? null : (
          <MuiMenuDropdown buttonText={"Actions"} ref={gridActionMenuRef}>
            <MenuItem
              onClick={(e) => {
                assignBudgetRow(e, params.row);
              }}
              disableRipple
              disabled={!canUserEditRows}
            >
              <AttachMoneyIcon />
              Edit
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                viewHistoryRow(e, params.row);
              }}
              disableRipple
            >
              <HistoryIcon />
              View History
            </MenuItem>
          </MuiMenuDropdown>
        );
      },
    });

    setGridColumns(columns);
  };

  const assignBudgetRow = (e, row) => {
    setBudgetEditorOpen(true);
    setCurrentRow(row);
    gridActionMenuRef.current.handleCloseExternal();
  };

  const saveBudgetValue = (reason, bucket) => {
    if (
      (bucket &&
        (editedBudgetValue === null || editedBudgetValue === undefined)) ||
      (editedBudgetValue && !bucket) ||
      (!bucket &&
        (editedRegularBudgetValue === null ||
          editedRegularBudgetValue === undefined ||
          editedBILBudgetValue === null ||
          editedBILBudgetValue === undefined ||
          editedDisasterReliefBudgetValue === null ||
          editedDisasterReliefBudgetValue === undefined)) ||
      !currentRow
    ) {
      return;
    }

    let department = currentRow[FieldNames.budgetFields.department]
      ? currentRow[FieldNames.budgetFields.department][
          FieldNames.budgetFields.department
        ]
        ? currentRow[FieldNames.budgetFields.department][
            FieldNames.budgetFields.department
          ]
        : currentRow[FieldNames.budgetFields.department]
      : "";
    let agency = currentRow[FieldNames.budgetFields.agency]
      ? currentRow[FieldNames.budgetFields.agency][
          FieldNames.budgetFields.agency
        ]
        ? currentRow[FieldNames.budgetFields.agency][
            FieldNames.budgetFields.agency
          ]
        : currentRow[FieldNames.budgetFields.agency]
      : "";
    let region = currentRow[FieldNames.budgetFields.region]
      ? currentRow[FieldNames.budgetFields.region][
          FieldNames.budgetFields.region
        ]
        ? currentRow[FieldNames.budgetFields.region][
            FieldNames.budgetFields.region
          ]
        : currentRow[FieldNames.budgetFields.region]
      : "";
    let unit = currentRow[FieldNames.budgetFields.unit]
      ? currentRow[FieldNames.budgetFields.unit][FieldNames.budgetFields.unit]
        ? currentRow[FieldNames.budgetFields.unit][FieldNames.budgetFields.unit]
        : currentRow[FieldNames.budgetFields.unit]
      : "";
    let subunit = currentRow[FieldNames.budgetFields.subUnit]
      ? currentRow[FieldNames.budgetFields.subUnit][
          FieldNames.budgetFields.subUnit
        ]
        ? currentRow[FieldNames.budgetFields.subUnit][
            FieldNames.budgetFields.subUnit
          ]
        : currentRow[FieldNames.budgetFields.subUnit]
      : "";
    let tribe = currentRow[FieldNames.budgetFields.tribe]
      ? currentRow[FieldNames.budgetFields.tribe][FieldNames.budgetFields.tribe]
        ? currentRow[FieldNames.budgetFields.tribe][
            FieldNames.budgetFields.tribe
          ]
        : currentRow[FieldNames.budgetFields.tribe]
      : "";

    let regBudget = currentRow[FieldNames.budgetFields.regularBudget] ?? 0;
    let bilBudget = currentRow[FieldNames.budgetFields.bilBudget] ?? 0;
    let disRelBudget =
      currentRow[FieldNames.budgetFields.disasterReliefBudget] ?? 0;

    if (bucket) {
      if (bucket === FieldNames.budgetBuckets.regular) {
        regBudget = Number(editedBudgetValue);
      } else if (bucket === FieldNames.budgetBuckets.bil) {
        bilBudget = Number(editedBudgetValue);
      } else if (bucket === FieldNames.budgetBuckets.disasterRelief) {
        disRelBudget = Number(editedBudgetValue);
      }
    } else {
      regBudget = editedRegularBudgetValue ?? regBudget;
      bilBudget = editedBILBudgetValue ?? bilBudget;
      disRelBudget = editedDisasterReliefBudgetValue ?? disRelBudget;
    }

    let budgetObj = {
      FiscalYear: Number(fiscalYearSelection.FiscalYear),
      EntityType: entityTypeSelection,
      Department: department,
      Agency: agency,
      Region: region,
      Unit: unit,
      SubUnit: subunit,
      Tribe: tribe,
      RegularBudget: regBudget,
      BILBudget: bilBudget,
      DisasterReliefBudget: disRelBudget,
      CreatedBy: currentRow.CreatedBy
        ? currentRow.CreatedBy
        : currentUserInfo[FieldNames.userFields.nifcUsername],
      CreatedOnDate: currentRow.CreatedOnDate
        ? currentRow.CreatedOnDate
        : getSaveableDate(new Date()),
      LastModifiedBy: currentUserInfo[FieldNames.userFields.nifcUsername],
      LastModifiedDate: getSaveableDate(new Date()),
      IsArchived: FieldNames.yesNo.no,
    };

    if (currentRow.OBJECTID) {
      budgetObj[FieldNames.featureFields.objectID] =
        currentRow[FieldNames.featureFields.objectID];
    }

    if (currentRow[FieldNames.featureFields.id]) {
      budgetObj[FieldNames.featureFields.id] =
        currentRow[FieldNames.featureFields.id];
    }

    if (currentRow[FieldNames.featureFields.objectID]) {
      // existing budget entry
      saveBudgetUpdate(budgetObj, reason);
    } else {
      // dummy row, create new budget entry
      saveNewBudget(budgetObj, reason);
    }
  };

  useEffect(() => {
    if (currentRow) {
      if (budgetBucketSelection) {
        if (
          budgetBucketSelection === FieldNames.budgetBuckets.regular &&
          currentRow[FieldNames.budgetFields.regularBudget]
        ) {
          setEditedBudgetValue(
            currentRow[FieldNames.budgetFields.regularBudget]
          );
        } else if (
          budgetBucketSelection === FieldNames.budgetBuckets.bil &&
          currentRow[FieldNames.budgetFields.bilBudget]
        ) {
          setEditedBudgetValue(currentRow[FieldNames.budgetFields.bilBudget]);
        } else if (
          budgetBucketSelection === FieldNames.budgetBuckets.disasterRelief &&
          currentRow[FieldNames.budgetFields.disasterReliefBudget]
        ) {
          setEditedBudgetValue(
            currentRow[FieldNames.budgetFields.disasterReliefBudget]
          );
        }
      } else {
        if (currentRow[FieldNames.budgetFields.regularBudget]) {
          setEditedRegularBudgetValue(
            currentRow[FieldNames.budgetFields.regularBudget]
          );
        }
        if (currentRow[FieldNames.budgetFields.bilBudget]) {
          setEditedBILBudgetValue(
            currentRow[FieldNames.budgetFields.bilBudget]
          );
        }
        if (currentRow[FieldNames.budgetFields.disasterReliefBudget]) {
          setEditedDisasterReliefBudgetValue(
            currentRow[FieldNames.budgetFields.disasterReliefBudget]
          );
        }
      }
    }
  }, [currentRow, budgetBucketSelection]);

  const saveNewBudget = (budget, reason) => {
    BudgetService.addBudgetEntry(
      budget
    ).then((response) => {
      if (response && response.addResults[0].success) {
        IDService.getBudgetID(response.addResults[0].objectId).then(idVal => {
          AuditLogService.addAuditLogEntry(
            { ID: idVal, ObjectID: response.addResults[0][FieldNames.featureFields.objectID]},
            idVal,
            FieldNames.auditLogJoinTypes.budget,
            currentUserInfo[FieldNames.userFields.nifcUsername],
            [reason]
          ).then((response) => {
            getGridBudgetRows();
            setCurrentRow(null);
            setEditedBudgetValue(0);
            setEditedRegularBudgetValue(0);
            setEditedBILBudgetValue(0);
            setEditedDisasterReliefBudgetValue(0);
            setBudgetBucketSelection(null);
            setBudgetReasoning("");
          }).catch(error => {
            ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
          });
        }).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
    });
  };

  const saveBudgetUpdate = (budget, reason) => {
    BudgetService.updateBudgetEntry(
      budget
    ).then((response) => {
      if (response && response.updateResults[0].success) {
        AuditLogService.addAuditLogEntry(
          { ID: budget[FieldNames.featureFields.id], ObjectID: response.updateResults[0][FieldNames.featureFields.objectID]},
          budget[FieldNames.featureFields.id],
          FieldNames.auditLogJoinTypes.budget,
          currentUserInfo[FieldNames.userFields.nifcUsername],
          [reason]
        ).then((response) => {
          getGridBudgetRows();
          setCurrentRow(null);
          setEditedBudgetValue(0);
          setEditedRegularBudgetValue(0);
          setEditedBILBudgetValue(0);
          setEditedDisasterReliefBudgetValue(0);
          setBudgetBucketSelection(null);
          setBudgetReasoning("");
        }).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
    });
  };

  const bulkAssignValues = () => {
    let objArrayToAdd = [];
    let objArrayToUpdate = [];

    if (
      editedBudgetBulkValue === null ||
      editedBudgetBulkValue === undefined ||
      !budgetBucketBulkSelection ||
      !allSelectedItems ||
      allSelectedItems.length === 0
    ) {
      return;
    }

    let year = Number(fiscalYearSelection.FiscalYear);

    allSelectedItems.forEach((budgetRow) => {
      let department = budgetRow[FieldNames.budgetFields.department]
        ? budgetRow[FieldNames.budgetFields.department][
            FieldNames.budgetFields.department
          ]
          ? budgetRow[FieldNames.budgetFields.department][
              FieldNames.budgetFields.department
            ]
          : budgetRow[FieldNames.budgetFields.department]
        : "";
      let agency = budgetRow[FieldNames.budgetFields.agency]
        ? budgetRow[FieldNames.budgetFields.agency][
            FieldNames.budgetFields.agency
          ]
          ? budgetRow[FieldNames.budgetFields.agency][
              FieldNames.budgetFields.agency
            ]
          : budgetRow[FieldNames.budgetFields.agency]
        : "";
      let region = budgetRow[FieldNames.budgetFields.region]
        ? budgetRow[FieldNames.budgetFields.region][
            FieldNames.budgetFields.region
          ]
          ? budgetRow[FieldNames.budgetFields.region][
              FieldNames.budgetFields.region
            ]
          : budgetRow[FieldNames.budgetFields.region]
        : "";
      let unit = budgetRow[FieldNames.budgetFields.unit]
        ? budgetRow[FieldNames.budgetFields.unit][FieldNames.budgetFields.unit]
          ? budgetRow[FieldNames.budgetFields.unit][
              FieldNames.budgetFields.unit
            ]
          : budgetRow[FieldNames.budgetFields.unit]
        : "";
      let subunit = budgetRow[FieldNames.budgetFields.subUnit]
        ? budgetRow[FieldNames.budgetFields.subUnit][
            FieldNames.budgetFields.subUnit
          ]
          ? budgetRow[FieldNames.budgetFields.subUnit][
              FieldNames.budgetFields.subUnit
            ]
          : budgetRow[FieldNames.budgetFields.subUnit]
        : "";
      let tribe = budgetRow[FieldNames.budgetFields.tribe]
        ? budgetRow[FieldNames.budgetFields.tribe][
            FieldNames.budgetFields.tribe
          ]
          ? budgetRow[FieldNames.budgetFields.tribe][
            FieldNames.budgetFields.tribe
          ]
          : budgetRow[FieldNames.budgetFields.tribe]
        : "";

      let budgetObj = {
        FiscalYear: year,
        EntityType: entityTypeSelection,
        Department: department,
        Agency: agency,
        Region: region,
        Unit: unit,
        SubUnit: subunit,
        Tribe: tribe,
        RegularBudget:
          budgetBucketBulkSelection === FieldNames.budgetBuckets.regular
            ? Number(editedBudgetBulkValue)
            : budgetRow[FieldNames.budgetFields.regularBudget]
            ? budgetRow[FieldNames.budgetFields.regularBudget]
            : 0,
        BILBudget:
          budgetBucketBulkSelection === FieldNames.budgetBuckets.bil
            ? Number(editedBudgetBulkValue)
            : budgetRow[FieldNames.budgetFields.bilBudget]
            ? budgetRow[FieldNames.budgetFields.bilBudget]
            : 0,
        DisasterReliefBudget:
          budgetBucketBulkSelection === FieldNames.budgetBuckets.disasterRelief
            ? Number(editedBudgetBulkValue)
            : budgetRow[FieldNames.budgetFields.disasterReliefBudget]
            ? budgetRow[FieldNames.budgetFields.disasterReliefBudget]
            : 0,
        CreatedBy: budgetRow.CreatedBy
          ? budgetRow.CreatedBy
          : currentUserInfo[FieldNames.userFields.nifcUsername],
        CreatedOnDate: budgetRow.CreatedOnDate
          ? budgetRow.CreatedOnDate
          : getSaveableDate(new Date()),
        LastModifiedBy: currentUserInfo[FieldNames.userFields.nifcUsername],
        LastModifiedDate: getSaveableDate(new Date()),
        IsArchived: FieldNames.yesNo.no,
      };

      if (budgetRow[FieldNames.featureFields.id]) {
        budgetObj[FieldNames.featureFields.id] =
          budgetRow[FieldNames.featureFields.id];
      }

      if (budgetRow[FieldNames.featureFields.objectID]) {
        budgetObj[FieldNames.featureFields.objectID] =
          budgetRow[FieldNames.featureFields.objectID];

        objArrayToUpdate.push(budgetObj);
      } else {
        objArrayToAdd.push(budgetObj);
      }
    });

    if (objArrayToAdd.length > 0) {
      bulkSaveNewBudget(objArrayToAdd, budgetBulkReasoning);
    }
    if (objArrayToUpdate.length > 0) {
      bulkSaveBudgetUpdate(objArrayToUpdate, budgetBulkReasoning);
    }
  };

  const bulkSaveNewBudget = (addList, reason) => {
    BudgetService.addMultipleBudgets(
      addList
    ).then((response) => {
      if (response && response.successful && response.successful.length > 0) {
        let objIDArray = response.successful;
        IDService.getMultipleBudgetIDs(objIDArray).then(idValList => {
          AuditLogService.addMultipleAuditLogs(
            FieldNames.auditLogJoinTypes.budget,
            addList,
            currentUserInfo[FieldNames.userFields.nifcUsername],
            reason
          ).then((response) => {
            getGridBudgetRows();
            setEditedBudgetBulkValue(0);
            setBudgetBucketBulkSelection(null);
            setBudgetBulkReasoning("");
          }).catch(error => {
            ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
          });
        }).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
    });
  };

  const bulkSaveBudgetUpdate = (updateList, reason) => {
    BudgetService.updateMultipleBudgets(
      updateList
    ).then((response) => {
      if (response && response.successful && response.successful.length > 0) {
        let objIDArray = response.successful;
        AuditLogService.addMultipleAuditLogs(
          FieldNames.auditLogJoinTypes.budget,
          updateList,
          currentUserInfo[FieldNames.userFields.nifcUsername],
          reason
        ).then((response) => {
          getGridBudgetRows();
          setEditedBudgetBulkValue(0);
          setBudgetBucketBulkSelection(null);
          setBudgetBulkReasoning("");
        }).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
    });
  };

  const viewHistoryRow = (e, row) => {
    establishHistoryGridColumns();
    getBudgetAuditLog(row[FieldNames.featureFields.id]);
    gridActionMenuRef.current.handleCloseExternal();
  };

  const establishHistoryGridColumns = () => {
    let columns = [];
    budgetAuditLogGridColDefinitions.forEach((colDef) => {
      columns.push({
        field: colDef.value,
        headerName: colDef.header,
        type: colDef.type,
        hideable: colDef.hideable,
        sortable: colDef.sortable,
        editable: colDef.editable,
        flex: colDef.flex,
        align: colDef.align,
        disableExport: colDef.disableExport,
        valueGetter: (value) => {
          return value; // Used for formatting string values I.E. currency formatting
        },
      });
    });

    setBudgetHistoryCols(columns);
  };

  //
  // General Page Methods
  //

  const handleBudgetReasonChange = (event) => {
    setBudgetReasoning(event.target.value);
  };

  const handleBulkBudgetReasonChange = (event) => {
    setBudgetBulkReasoning(event.target.value);
  };

  const debounceBudgetReasonFn = useCallback(
    _debounce(handleBudgetReasonChange, 300),
    []
  );

  const debounceBulkBudgetReasonFn = useCallback(
    _debounce(handleBulkBudgetReasonChange, 300),
    []
  );

  const handleClose = () => {
    setShowMap(true);
    navigate("/home");
  };

  const closeBudgetEditor = () => {
    setBudgetEditorOpen(false);
    setCurrentRow(null);
  };

  const closeBudgetHistory = () => {
    setBudgetHistoryOpen(false);
  };

  const closeBulkBudgetAssignCheck = () => {
    setBulkConfirmOpen(false);
  };

  const confirmBulkBudgetAssignCheck = () => {
    setBulkConfirmOpen(false);
    bulkAssignValues();
  };

  const getBudgetAuditLog = (joinID) => {
    if (joinID) {
      AuditLogService.getEntityAuditLog(
        FieldNames.auditLogJoinTypes.budget,
        joinID
      ).then((auditRows) => {
        if (auditRows && auditRows.length > 0) {
          var mappedRows = auditRows.map((auditRow) => {
            try{
              return {
                id: auditRow[FieldNames.featureFields.objectID],
                ModifiedBy: auditRow.ModifiedBy,
                ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                Modification: JSON.parse(auditRow.AuditEntryJson)[0],
              };
            } catch(e){
              return {
                id: auditRow[FieldNames.featureFields.objectID],
                ModifiedBy: auditRow.ModifiedBy,
                ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                Modification: auditRow.AuditEntryJson,
              };
            }
          });
          setBudgetHistoryRows(mappedRows);
        } else {
          setBudgetHistoryRows([]);
        }
        setBudgetHistoryOpen(true);
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else {
      setBudgetHistoryRows([]);
      setBudgetHistoryOpen(true);
    }
  };

  // --- Grid Logic ---

  const exportOptions = {
    getRowsToExport: () => gridFilteredSortedRowIdsSelector(apiRef),
  };

  function CustomExportContainer(props) {
    return (
      <GridToolbarExportContainer {...props}>
        <GridCsvExportMenuItem
          options={exportOptions}
          onClick={() => apiRef.current.exportDataAsCsv(exportOptions)}
        />
        <GridExcelExportMenuItem
          options={exportOptions}
          onClick={() => apiRef.current.exportDataAsExcel(exportOptions)}
        />
      </GridToolbarExportContainer>
    );
  }

  function CustomBudgetGridToolbar(props) {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <CustomExportContainer />
      </GridToolbarContainer>
    );
  }

  return (
    <Box>
      <AppBar
        sx={{ position: "relative" }}
        style={{ backgroundColor: BlueAlien, marginTop: headerheight }}
      >
        <Toolbar>
          <FormControl variant="standard" className={classes.formControl}>
            <ThemeProvider theme={theme}>
              <Grid
                item
                size={12}
                container
                direction="row"
                className="grid-item"
                style={{ alignItems: "center" }}
              >
                <Grid item size={0.2}>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                  >
                    <CloseIcon style={{ fill: WhiteAlien }} />
                  </IconButton>
                </Grid>
                <Grid item size={4}>
                  <Typography
                    sx={{ ml: 2, flex: 1 }}
                    variant="h6"
                    component="div"
                    style={{ color: WhiteAlien }}
                  >
                    Program of Work Budget
                  </Typography>
                </Grid>
                <WorkflowFiscalYearFilter
                  fiscalYearSelection={fiscalYearSelection}
                  setFiscalYearSelection={setFiscalYearSelection}
                  fiscalYearOptions={fiscalYearOptions}
                />
              </Grid>
            </ThemeProvider>
          </FormControl>
        </Toolbar>
      </AppBar>
      <Paper style={{ maxHeight: "85vh", overflow: "auto" }}>
        <Box
          sx={{
            height: currentSelectedLevel === "Master Level" ? "200px" : 
                    (fundingAgencySelection && fundingAgencySelection[FieldNames.budgetFields.agency] &&
                      (fundingAgencySelection[FieldNames.budgetFields.agency] === FieldNames.agencies.blm ||
                      fundingAgencySelection[FieldNames.budgetFields.agency] === FieldNames.agencies.bia)
                     ? "450px" : "400"),
            width: "100%",
            minWidth: "10px",
          }}
        >
          <WorkflowGridFilters
            currentUserInfo={currentUserInfo}
            userRoleInfo={userRoleInfo}
            fundingDepartmentSelection={fundingDepartmentSelection}
            setFundingDepartmentSelection={setFundingDepartmentSelection}
            fundingAgencySelection={fundingAgencySelection}
            setFundingAgencySelection={setFundingAgencySelection}
            fundingRegionSelection={fundingRegionSelection}
            setFundingRegionSelection={setFundingRegionSelection}
            fundingUnitSelection={fundingUnitSelection}
            setFundingUnitSelection={setFundingUnitSelection}
            fundingSubUnitSelection={fundingSubUnitSelection}
            setFundingSubUnitSelection={setFundingSubUnitSelection}
            fundingTribeSelection={fundingTribeSelection}
            setFundingTribeSelection={setFundingTribeSelection}
            entityTypeSelection={entityTypeSelection}
            setEntityTypeSelection={setEntityTypeSelection}
            setColumnVisibilityModel={setColumnVisibilityModel}
          />
          <>
            {currentSelectedLevel === "Master Level" ? null : (
              <FormControl variant="standard" className={classes.formControl}>
                <ThemeProvider theme={theme}>
                  <Grid
                    item
                    size={12}
                    
                    container
                    direction="row"
                    className="grid-item"
                    style={{ alignItems: "center" }}
                  >
                    <Grid item size={5}>
                      <span>
                        <Typography sx={{ fontWeight: "bold" }}>{`FY ${
                          fiscalYearSelection
                            ? fiscalYearSelection.FiscalYear
                            : "XXXX"
                        } Allocation for ${currLevelLabel} '${currentSelectedLevel}'`}</Typography>
                      </span>
                    </Grid>
                    <Grid item size={1} />
                    <Grid item size={5}>
                      <span>
                        <Typography sx={{ fontWeight: "bold" }}>{`FY ${
                          fiscalYearSelection
                            ? fiscalYearSelection.FiscalYear
                            : "XXXX"
                        } Balance for ${currLevelLabel} '${currentSelectedLevel}'`}</Typography>
                      </span>
                    </Grid>
                    <Grid item size={1} />
                    {/* Next Line */}
                    <Grid item size={2} />
                    <Grid item size={4}>
                      <Stack>
                        <Grid container>
                          <Grid item size={4.5} style={{ marginTop: "7.5px" }}>
                            <span>
                              <label>Regular: </label>
                            </span>
                          </Grid>
                          <Grid item size={5.5}>
                            <Input
                              id="outlined-adornment-amount"
                              disabled
                              value={formattedRegularBudgetAllocation}
                              variant="standard"
                              startAdornment={
                                <InputAdornment
                                  style={{ marginTop: "0" }}
                                  position="start"
                                >
                                  $
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item size={4.5} style={{ marginTop: "7.5px" }}>
                            <span>
                              <label>BIL: </label>
                            </span>
                          </Grid>
                          <Grid item size={5.5}>
                            <Input
                              id="outlined-adornment-amount"
                              disabled
                              value={formattedBilBudgetAllocation}
                              variant="standard"
                              startAdornment={
                                <InputAdornment
                                  style={{ marginTop: "0" }}
                                  position="start"
                                >
                                  $
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item size={4.5} style={{ marginTop: "7.5px" }}>
                            <span>
                              <label>Disaster Relief: </label>
                            </span>
                          </Grid>
                          <Grid item size={5.5}>
                            <Input
                              id="outlined-adornment-amount"
                              disabled
                              value={formattedDisasterReliefBudgetAllocation}
                              variant="standard"
                              startAdornment={
                                <InputAdornment
                                  style={{ marginTop: "0" }}
                                  position="start"
                                >
                                  $
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid
                            item
                            size={4.5}
                            style={{ marginTop: "7.5px", fontWeight: "bold" }}
                          >
                            <span>
                              <label>Total Allocation: </label>
                            </span>
                          </Grid>
                          <Grid item size={5.5}>
                            <Input
                              id="outlined-adornment-amount"
                              disabled
                              value={formattedTotalBudgetAllocation}
                              variant="standard"
                              startAdornment={
                                <InputAdornment
                                  style={{ marginTop: "0" }}
                                  position="start"
                                >
                                  $
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Grid>
                    <Grid item size={2} />
                    <Grid item size={4}>
                      <Stack>
                        <Grid container>
                          <Grid item size={4.5} style={{ marginTop: "7.5px" }}>
                            <span>
                              <label>Regular: </label>
                            </span>
                          </Grid>
                          <Grid item size={5.5}>
                            <Input
                              id="outlined-adornment-amount"
                              disabled
                              value={formattedRemainingRegularBudgetValue}
                              style={{
                                color:
                                  remainingRegularBudgetValue < 0
                                    ? "red"
                                    : "grey",
                              }}
                              variant="standard"
                              startAdornment={
                                <InputAdornment
                                  style={{ marginTop: "0" }}
                                  position="start"
                                >
                                  $
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item size={4.5} style={{ marginTop: "7.5px" }}>
                            <span>
                              <label>BIL: </label>
                            </span>
                          </Grid>
                          <Grid item size={5.5}>
                            <Input
                              id="outlined-adornment-amount"
                              disabled
                              value={formattedRemainingBilBudgetValue}
                              style={{
                                color:
                                  remainingBILBudgetValue < 0 ? "red" : "grey",
                              }}
                              variant="standard"
                              startAdornment={
                                <InputAdornment
                                  style={{ marginTop: "0" }}
                                  position="start"
                                >
                                  $
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item size={4.5} style={{ marginTop: "7.5px" }}>
                            <span>
                              <label>Disaster Relief: </label>
                            </span>
                          </Grid>
                          <Grid item size={5.5}>
                            <Input
                              id="outlined-adornment-amount"
                              disabled
                              value={formattedRemainingDisasterReliefBudgetValue}
                              style={{
                                color:
                                  remainingDisasterReliefBudgetValue < 0
                                    ? "red"
                                    : "grey",
                              }}
                              variant="standard"
                              startAdornment={
                                <InputAdornment
                                  style={{ marginTop: "0" }}
                                  position="start"
                                >
                                  $
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid
                            item
                            size={4.5}
                            style={{ marginTop: "7.5px", fontWeight: "bold" }}
                          >
                            <span>
                              <label>Total Balance: </label>
                            </span>
                          </Grid>
                          <Grid item size={5.5}>
                            <Input
                              id="outlined-adornment-amount"
                              disabled
                              value={formattedRemainingTotalBudgetValue}
                              style={{
                                color:
                                  remainingTotalBudgetValue < 0
                                    ? "red"
                                    : "grey",
                              }}
                              variant="standard"
                              startAdornment={
                                <InputAdornment
                                  style={{ marginTop: "0" }}
                                  position="start"
                                >
                                  $
                                </InputAdornment>
                              }
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Grid>
                    {/* New Line */}
                    <Grid item size={2} />
                    <Grid item size={3.3}>
                      <Box sx={{ backgroundColor: "#D3D3D3", marginTop: "10px"}}
                          style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap'}}>
                        <HelpIcon fontSize="small" color="primary" style={{ paddingRight: "5px" }}/>
                        <Typography fontSize={12}>
                          Allocations assigned at the level above you
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item size={2.7} />
                    <Grid item size={3.3}>
                      <Box sx={{ backgroundColor: "#D3D3D3", marginTop: "10px"}}
                      style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap'}}>
                        <HelpIcon fontSize="small" color="primary" style={{ paddingRight: "5px" }}/>
                        <Typography fontSize={12}>
                          Balance after assigning allocations to levels below you
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </ThemeProvider>
              </FormControl>
            )}
          </>
        </Box>
        <InFormGenericModal
          openFlag={budgetEditorOpen}
          closeMethod={closeBudgetEditor}
          titleText={"Edit Budget"}
          displayButton={false}
        >
          <Box
            style={{
              maxHeight: "300px",
              overflow: "auto",
              width: "33%",
              minWidth: "450px",
            }}
          >
            <FormControl variant="standard" className={classes.formControl}>
              {/* <ThemeProvider theme={theme}>
                <Grid
                  item
                  size={12}
                  
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  <Grid item size={12}>
                    <Typography display={"inline"}>Funding Subtype:{" "}</Typography>
                    <Typography display={"inline"} color={"red"}>
                      {" "}
                      *
                    </Typography>
                  </Grid>
                  <Grid item size={12}>
                    <Autocomplete
                      //multiple
                      disableClearable
                      id="budgetBucketMulti"
                      open={budgetBucketOpen}
                      value={budgetBucketSelection}
                      onChange={(event, newValue) => {
                        if (newValue === "N/A") {
                          newValue = "";
                        }
                        setBudgetBucketSelection(newValue);
                      }}
                      onOpen={() => {
                        setBudgetBucketOpen(true);
                      }}
                      onClose={() => {
                        setBudgetBucketOpen(false);
                      }}
                      isOptionEqualToValue={(option, value) => {
                        return option === value || value === "";
                      }}
                      getOptionLabel={(option) => {
                        return option;
                      }}
                      options={budgetBucketOptions}
                      getOptionDisabled={(option) => {
                        if (budgetBucketSelection) {
                          const selected = budgetBucketSelection;
                          if (selected && option && selected === option) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          return false;
                        }
                      }}
                      loading={budgetBucketLoading}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option[FieldNames.units.tribeFullName]}
                            {...getTagProps({ index })}
                            disabled={false}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {budgetBucketLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item size={3}>
                    <Typography display={"inline"}>Budget:{" "}</Typography>
                    <Typography display={"inline"} color={"red"}>
                      {" "}
                      *
                    </Typography>
                  </Grid>
                  <Grid item size={6}>
                    <Input
                      id="outlined-adornment-amount"
                      value={editedBudgetValue}
                      variant="standard"
                      onChange={(event) => {
                        const decimalFreeValue = removeDecimalWithoutDefault(
                          event.target.value
                        );
                        const negativeFreeValue =
                          removeNegativeWithoutDefault(decimalFreeValue);
                        const letterFreeValue =
                          removeLettersWithoutDefault(negativeFreeValue);
                        const commasAdded = currencyFormat(letterFreeValue);
                        setEditedBudgetValue(commasAdded);
                      }}
                      startAdornment={
                        <InputAdornment
                          style={{ marginTop: "0" }}
                          position="start"
                        >
                          $
                        </InputAdornment>
                      }
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  size={12}
                  
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  <Grid item size={12}>
                    <TextField
                      multiline
                      sx={{ minWidth: 240 }}
                      style={{ marginTop: 10 }}
                      onChange={(event) => {
                        debounceBudgetReasonFn(event);
                      }}
                      fullWidth={true}
                      variant="outlined"
                      placeholder="Enter reason for budget change"
                    />
                  </Grid>
                </Grid>
              </ThemeProvider> */}
              <ThemeProvider theme={theme}>
                <Grid
                  item
                  size={12}
                  
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  <Grid item size={6}>
                    <Typography display={"inline"}>Regular Budget: </Typography>
                    <Typography display={"inline"} color={"red"}>
                      {" "}
                      *
                    </Typography>
                  </Grid>
                  <Grid item size={6}>
                    <Input
                      id="outlined-adornment-amount"
                      value={editedRegularBudgetValue}
                      variant="standard"
                      onChange={(event) => {
                        const decimalFreeValue = removeDecimalWithoutDefault(
                          event.target.value
                        );
                        const negativeFreeValue =
                          removeNegativeWithoutDefault(decimalFreeValue);
                        const letterFreeValue =
                          removeLettersWithoutDefault(negativeFreeValue);
                        const commasAdded = currencyFormat(letterFreeValue);
                        setEditedRegularBudgetValue(commasAdded);
                      }}
                      startAdornment={
                        <InputAdornment
                          style={{ marginTop: "0" }}
                          position="start"
                        >
                          $
                        </InputAdornment>
                      }
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  size={12}
                  
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  <Grid item size={6}>
                    <Typography display={"inline"}>BIL Budget: </Typography>
                    <Typography display={"inline"} color={"red"}>
                      {" "}
                      *
                    </Typography>
                  </Grid>
                  <Grid item size={6}>
                    <Input
                      id="outlined-adornment-amount"
                      value={editedBILBudgetValue}
                      variant="standard"
                      onChange={(event) => {
                        const decimalFreeValue = removeDecimalWithoutDefault(
                          event.target.value
                        );
                        const negativeFreeValue =
                          removeNegativeWithoutDefault(decimalFreeValue);
                        const letterFreeValue =
                          removeLettersWithoutDefault(negativeFreeValue);
                        const commasAdded = currencyFormat(letterFreeValue);
                        setEditedBILBudgetValue(commasAdded);
                      }}
                      startAdornment={
                        <InputAdornment
                          style={{ marginTop: "0" }}
                          position="start"
                        >
                          $
                        </InputAdornment>
                      }
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  size={12}
                  
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  <Grid item size={6}>
                    <Typography display={"inline"}>
                      Disaster Relief Budget:{" "}
                    </Typography>
                    <Typography display={"inline"} color={"red"}>
                      {" "}
                      *
                    </Typography>
                  </Grid>
                  <Grid item size={6}>
                    <Input
                      id="outlined-adornment-amount"
                      value={editedDisasterReliefBudgetValue}
                      variant="standard"
                      onChange={(event) => {
                        const decimalFreeValue = removeDecimalWithoutDefault(
                          event.target.value
                        );
                        const negativeFreeValue =
                          removeNegativeWithoutDefault(decimalFreeValue);
                        const letterFreeValue =
                          removeLettersWithoutDefault(negativeFreeValue);
                        const commasAdded = currencyFormat(letterFreeValue);
                        setEditedDisasterReliefBudgetValue(commasAdded);
                      }}
                      startAdornment={
                        <InputAdornment
                          style={{ marginTop: "0" }}
                          position="start"
                        >
                          $
                        </InputAdornment>
                      }
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  size={12}
                  
                  container
                  direction="row"
                  className="grid-item"
                  style={{ alignItems: "center" }}
                >
                  <Grid item size={12}>
                    <BasicTextField
                      field="budgetReason"
                      label=""
                      setValue={setBudgetReasoning}
                      variant={"outlined"}
                      placeholder={"Enter reason for budget change"}
                      useFullWidth={true}
                      useMultiline={true}
                    />
                  </Grid>
                </Grid>
              </ThemeProvider>
            </FormControl>
            <Box sx={{ marginLeft: "60%", marginBottom: "5px" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setCurrentRow(null);
                  setEditedBudgetValue(0);
                  setEditedRegularBudgetValue(0);
                  setEditedBILBudgetValue(0);
                  setEditedDisasterReliefBudgetValue(0);
                  setBudgetBucketSelection(null);
                  setBudgetReasoning("");
                  setBudgetEditorOpen(false);
                }}
                aria-label="cancel"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  saveBudgetValue(budgetReasoning, budgetBucketSelection);
                  setBudgetEditorOpen(false);
                }}
                disabled={
                  (editedBudgetValue && !budgetBucketSelection) ||
                  (!editedBudgetValue && editedBudgetValue !== 0) ||
                  (!budgetBucketSelection &&
                    ((!editedRegularBudgetValue &&
                      editedRegularBudgetValue !== 0) ||
                      (!editedBILBudgetValue && editedBILBudgetValue !== 0) ||
                      (!editedDisasterReliefBudgetValue &&
                        editedDisasterReliefBudgetValue !== 0)))
                }
                aria-label="edit"
                sx={{ marginLeft: "5px" }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </InFormGenericModal>
        <InFormGenericModal
          openFlag={budgetHistoryOpen}
          closeMethod={closeBudgetHistory}
          titleText={"Budget History"}
          displayButton={false}
        >
          <Box
            style={{
              maxHeight: "300px",
              overflow: "auto",
              width: "66%",
              minWidth: "800px",
            }}
          >
            <FormControl variant="standard" className={classes.formControl}>
              <ThemeProvider theme={theme}>
                <Box sx={{ height: "250px", width: "100%", minWidth: "10px" }}>
                  <DataGridPremium
                    rows={budgetHistoryRows}
                    columns={budgetHistoryCols}
                  ></DataGridPremium>
                </Box>
              </ThemeProvider>
            </FormControl>
            <Box sx={{ marginLeft: "80%", marginBottom: "5px" }}>
              <Button
                variant="outlined"
                onClick={() => setBudgetHistoryOpen(false)}
                aria-label="cancel"
              >
                Close
              </Button>
            </Box>
          </Box>
        </InFormGenericModal>
        <InFormGenericModal
          openFlag={bulkConfirmOpen}
          closeMethod={closeBulkBudgetAssignCheck}
          titleText={"Confirm Budget Bulk Assignment"}
          displayButton={false}
        >
          <Box
            style={{
              maxHeight: "300px",
              overflow: "auto",
              width: "33%",
              minWidth: "450px",
            }}
          >
            <Box>
              <Typography
                sx={{ marginLeft: "5px" }}
                fontWeight={"bold"}
                display="inline"
              >
                Funding Subtype:{" "}
              </Typography>
              <Typography display="inline">
                {budgetBucketBulkSelection}
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{ marginLeft: "5px" }}
                fontWeight={"bold"}
                display="inline"
              >
                Budget:{" "}
              </Typography>
              <Typography display="inline">
                $ {editedBudgetBulkValue}
              </Typography>
            </Box>
            <Typography sx={{ marginLeft: "5px" }}>
              Are you sure you want to edit the budget for {gridSelectionLength}{" "}
              rows?
            </Typography>
          </Box>
          <Box sx={{ marginLeft: "60%", marginBottom: "5px" }}>
            <Button
              variant="outlined"
              onClick={() => {
                closeBulkBudgetAssignCheck();
                setEditedBudgetBulkValue(0);
                setBudgetBucketBulkSelection(null);
                setBudgetBulkReasoning("");
              }}
              aria-label="cancel"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                confirmBulkBudgetAssignCheck();
                setBudgetEditorOpen(false);
              }}
              aria-label="edit"
              sx={{ marginLeft: "5px" }}
            >
              Save
            </Button>
          </Box>
        </InFormGenericModal>
        <Box
          sx={{
            height: "525px",
            width: "100%",
            minWidth: "10px",
            paddingTop: "10px !important",
            paddingBottom: "60px !important"
          }}
        >
          <DataGridPremium
              showToolbar
            apiRef={apiRef}
            getRowId={(row) =>
              row.index +
              "-" +
              row[FieldNames.budgetFields.objectID] +
              "-" +
              row[FieldNames.budgetFields.constructedFieldName] +
              "-" +
              row[FieldNames.budgetFields.subUnit] +
              "-" +
              row[FieldNames.budgetFields.tribe]
            }
            rows={gridRows}
            columns={gridColumns}
            components={{
              Toolbar: CustomBudgetGridToolbar,
              loadingOverlay: LinearProgress,
            }}
            loading={isGridLoading}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            columnHeaderHeight={60}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(newSelection) => {
              setSelectionModel(newSelection);
              let selectedItems = [];
              newSelection.ids.forEach((item) => {
                const group = apiRef.current.getRowNode(item);
                let selectedGridValue;
                if (
                  group.groupingField
                ) {
                  const itemSplit = item.split('/');
                  const allItemsAfterIdentifier = itemSplit.slice(1);
                  const rejoinedValue = allItemsAfterIdentifier.join("/");
                  selectedGridValue = gridRows.filter(
                    (x) => x[group.groupingField] === rejoinedValue
                  );
                } else {
                  selectedGridValue = gridRows.filter(
                    (x) =>
                      x[FieldNames.budgetFields.objectID] +
                        "-" +
                        x[FieldNames.budgetFields.constructedFieldName] +
                        "-" +
                        x[FieldNames.budgetFields.subUnit] +
                        "-" +
                        x[FieldNames.budgetFields.tribe] ===
                      item
                  );
                }
                if (selectedGridValue && selectedGridValue.length > 0) {
                  selectedItems.push(selectedGridValue[0]);
                }
              });

              // Cost Calculations
              const budgetTotal = selectedItems
                .map(
                  (item) => item[FieldNames.budgetFields.constructedTotalBudget]
                )
                .reduce((a, b) => a + b, 0);

              setSelectedBudgetTotal(budgetTotal);
              setAllSelectedItems(selectedItems);
            }}
            experimentalFeatures={{ aggregation: true }}
            aggregationModel={aggregationModel}
            onAggregationModelChange={(newModel) => {
              setAggregationModel(newModel);
            }}
            sx={{
              "& .MuiDataGrid-aggregationColumnHeaderLabel": {
                lineHeight: "0.5rem",
              },
            }}
          />
          {/* <FormControl variant="standard" className={classes.formControl}>
            <ThemeProvider theme={theme}>
              <Grid
                item
                size={12}
                
                container
                direction="row"
                className="grid-item"
                style={{ alignItems: "center", marginBottom: "10px" }}
              >
                <Grid item size={0.75}>
                  <span>
                    <Typography display={"inline"}>Funding Subtype:</Typography>
                    <Typography display={"inline"} color={"red"}>
                      {" "}
                      *
                    </Typography>
                  </span>
                </Grid>
                <Grid item size={2.5} sx={{ marginRight: "5px" }}>
                  <Autocomplete
                    //multiple
                    disableClearable
                    id="budgetBucketMultiBulk"
                    open={budgetBucketBulkOpen}
                    value={budgetBucketBulkSelection}
                    onChange={(event, newValue) => {
                      if (newValue === "N/A") {
                        newValue = "";
                      }
                      setBudgetBucketBulkSelection(newValue);
                    }}
                    onOpen={() => {
                      setBudgetBucketBulkOpen(true);
                    }}
                    onClose={() => {
                      setBudgetBucketBulkOpen(false);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option === value || value === "";
                    }}
                    getOptionLabel={(option) => {
                      return option;
                    }}
                    options={budgetBucketOptions}
                    getOptionDisabled={(option) => {
                      if (budgetBucketBulkSelection) {
                        const selected = budgetBucketBulkSelection;
                        if (selected && option && selected === option) {
                          return true;
                        } else {
                          return false;
                        }
                      } else {
                        return false;
                      }
                    }}
                    loading={budgetBucketBulkLoading}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          disabled={false}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {budgetBucketLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item size={1.5}>
                  <span>
                    <Typography display={"inline"}>
                      Budget To Assign:
                    </Typography>
                    <Typography display={"inline"} color={"red"}>
                      {" "}
                      *
                    </Typography>
                  </span>
                </Grid>
                <Grid item size={2}>
                  <Input
                    id="outlined-adornment-amount"
                    value={editedBudgetBulkValue}
                    onChange={(event) => {
                      const decimalFreeValue = removeDecimalWithoutDefault(
                        event.target.value
                      );
                      const negativeFreeValue =
                        removeNegativeWithoutDefault(decimalFreeValue);
                      const letterFreeValue = removeLettersWithoutDefault(negativeFreeValue);
                      const commasAdded = currencyFormat(letterFreeValue);
                      setEditedBudgetBulkValue(commasAdded);
                    }}
                    variant="standard"
                    startAdornment={
                      <InputAdornment
                        style={{ marginTop: "0" }}
                        position="start"
                      >
                        $
                      </InputAdornment>
                    }
                  />
                </Grid>

                <Grid item size={2.75}>
                  <TextField
                    multiline
                    sx={{ minWidth: 240 }}
                    style={{ marginTop: 10 }}
                    onChange={(event) => {
                      debounceBulkBudgetReasonFn(event);
                    }}
                    fullWidth={true}
                    variant="outlined"
                    placeholder="Enter reason for budget change"
                  />
                </Grid>
                <Grid item size={0.5} />
                <Grid item size={1.5} sx={{ paddingTop: "5px" }}>
                  <Button
                    variant="contained"
                    disabled={
                      !budgetBucketBulkSelection ||
                      (!editedBudgetBulkValue && editedBudgetBulkValue !== 0) ||
                      editedBudgetBulkValue < 0 || 
                      (!allSelectedItems || allSelectedItems.length === 0)
                    }
                    onClick={() => setBulkConfirmOpen(true)}
                    aria-label="edit"
                  >
                    Bulk Assign
                  </Button>
                </Grid>
              </Grid>
            </ThemeProvider>
          </FormControl> */}
          <ThemeProvider theme={theme}>
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={6000}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              className={classes.snackBarStyle}
              onClose={() => {
                setSnackbarOpen(false);
                setSnackbarMessage("");
              }}
            >
              <Alert
                onClose={() => {
                  setSnackbarOpen(false);
                  setSnackbarMessage("");
                }}
                severity={isFailure ? "error" : "success"}
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </ThemeProvider>
        </Box>
      </Paper>
    </Box>
  );
}

BudgetManagementDialog.propTypes = {
  currentUserInfo: PropTypes.object.isRequired,
  userRoleInfo: PropTypes.array,
  setShowMap: PropTypes.func.isRequired,
};
